import { useEffect, useState } from "react";
import { Loader2, X, Plus, Pencil, BadgeCheck, Award, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtErr } from "../api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Config-driven CRUD for the attendance backend's Phase-1 master-data APIs.
// All three kinds support list/create/patch (incl. active flag). Hard delete stays unavailable here.
const KINDS = {
  designations: {
    title: "Designations", icon: BadgeCheck, endpoint: "/master-data/designations",
    nameOf: (it) => it.name,
    subOf: (it, grades) => it.grade_id ? `Grade: ${(grades.find((g) => g.id === it.grade_id) || {}).name || it.grade_id}` : "",
    empty: { name: "", grade_id: "" },
    toPayload: (f) => ({ name: f.name.trim(), grade_id: f.grade_id || null }),
    fields: [
      { key: "name", label: "Designation Name", type: "text", required: true, testid: "md-form-name" },
      { key: "grade_id", label: "Grade", type: "grade_select", testid: "md-form-grade" },
    ],
  },
  grades: {
    title: "Grades", icon: Award, endpoint: "/master-data/grades",
    nameOf: (it) => it.name || it.code,
    subOf: (it) => [it.code, it.sort_order != null ? `Order ${it.sort_order}` : ""].filter(Boolean).join(" · "),
    empty: { code: "", name: "", sort_order: "100" },
    toPayload: (f) => ({ code: f.code.trim(), name: f.name.trim(), sort_order: parseInt(f.sort_order, 10) || 100 }),
    fields: [
      { key: "code", label: "Grade Code", type: "text", required: true, testid: "md-form-code" },
      { key: "name", label: "Grade Name", type: "text", required: true, testid: "md-form-name" },
      { key: "sort_order", label: "Sort Order", type: "number", testid: "md-form-sort" },
    ],
  },
  shifts: {
    title: "Shifts", icon: Clock3, endpoint: "/master-data/shifts",
    nameOf: (it) => it.name,
    subOf: (it) => [it.in_time && it.out_time ? `${it.in_time} – ${it.out_time}` : "", it.break_min != null ? `Break ${it.break_min}m` : "", it.grace_min != null ? `Grace ${it.grace_min}m` : "", Array.isArray(it.weekly_off) && it.weekly_off.length ? `Off: ${it.weekly_off.map((d) => DAYS[d] ?? d).join(", ")}` : ""].filter(Boolean).join(" · "),
    empty: { name: "", in_time: "", out_time: "", break_min: "60", grace_min: "15", weekly_off: [6, 0] },
    toPayload: (f) => ({ name: f.name.trim(), in_time: f.in_time, out_time: f.out_time, break_min: parseInt(f.break_min, 10) || 0, grace_min: parseInt(f.grace_min, 10) || 0, weekly_off: f.weekly_off }),
    fields: [
      { key: "name", label: "Shift Name", type: "text", required: true, testid: "md-form-name" },
      { key: "in_time", label: "In Time", type: "time", required: true, testid: "md-form-in" },
      { key: "out_time", label: "Out Time", type: "time", required: true, testid: "md-form-out" },
      { key: "break_min", label: "Break (minutes)", type: "number", testid: "md-form-break" },
      { key: "grace_min", label: "Grace (minutes)", type: "number", testid: "md-form-grace" },
      { key: "weekly_off", label: "Weekly Off", type: "days", testid: "md-form-weeklyoff" },
    ],
  },
};

const MasterData = ({ kind }) => {
  const cfg = KINDS[kind];
  const [rows, setRows] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [form, setForm] = useState(cfg.empty);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const calls = [staffApi.get(cfg.endpoint)];
      if (kind === "designations") calls.push(staffApi.get("/master-data/grades"));
      const [r, g] = await Promise.allSettled(calls);
      if (r.status === "fulfilled") setRows(Array.isArray(r.value.data) ? r.value.data : []);
      else setError(fmtErr(r.reason));
      if (g && g.status === "fulfilled") setGrades(Array.isArray(g.value.data) ? g.value.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(cfg.empty); setFormError(""); setEditing("new"); };
  const openEdit = (it) => {
    const f = { ...cfg.empty };
    for (const k of Object.keys(cfg.empty)) f[k] = it[k] ?? cfg.empty[k];
    setForm(f);
    setFormError("");
    setEditing(it);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    setBusy(true);
    try {
      const payload = cfg.toPayload(form);
      if (editing === "new") {
        await staffApi.post(cfg.endpoint, payload);
        toast.success(`${cfg.title.slice(0, -1)} added.`);
      } else {
        await staffApi.patch(`${cfg.endpoint}/${editing.id}`, payload);
        toast.success(`${cfg.title.slice(0, -1)} updated.`);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (it) => {
    try {
      await staffApi.patch(`${cfg.endpoint}/${it.id}`, { active: !(it.active !== false) });
      toast.success(`${cfg.nameOf(it)} ${it.active === false ? "activated" : "deactivated"}.`);
      await load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const visible = rows.filter((it) => showInactive || it.active !== false);
  const Icon = cfg.icon;
  const inputCls = "w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]";

  return (
    <div data-testid={`master-data-${kind}`}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{cfg.title}</h1>
          <p className="mt-1 text-sm text-slate-500">Shared master data from the central HR system. Deactivate hides an entry from new assignments without deleting history.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="md-show-inactive" className="w-4 h-4 accent-[#C5221F]" />
            Show inactive
          </label>
          <button onClick={openNew} data-testid="md-add-btn"
            className="inline-flex items-center gap-1.5 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
            <Plus size={13} /> Add {cfg.title.slice(0, -1)}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="md-error">{error}</p>
      ) : visible.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="md-empty">No {cfg.title.toLowerCase()} configured yet.</p>
      ) : (
        <div className="mt-6 bg-white border border-slate-200" data-testid="md-list-card">
          <ul className="divide-y divide-slate-100" data-testid="md-list">
            {visible.map((it) => (
              <li key={it.id} className={`px-5 py-3.5 flex items-center justify-between gap-4 ${it.active === false ? "opacity-50" : ""}`} data-testid={`md-row-${it.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 shrink-0 bg-[#0A1128] text-white flex items-center justify-center">
                    <Icon size={14} className="text-[#E8574F]" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{cfg.nameOf(it)}</p>
                    {cfg.subOf(it, grades) && <p className="text-xs text-slate-400 truncate">{cfg.subOf(it, grades)}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => toggleActive(it)} data-testid={`md-toggle-${it.id}`}
                    className={`border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${it.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                    {it.active !== false ? "Active" : "Inactive"}
                  </button>
                  <button onClick={() => openEdit(it)} data-testid={`md-edit-${it.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A1128] hover:underline">
                    <Pencil size={11} /> Edit
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="md-form-modal" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md max-h-[85vh] overflow-y-auto p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900">{editing === "new" ? `Add ${cfg.title.slice(0, -1)}` : `Edit ${cfg.nameOf(editing)}`}</h2>
              <button onClick={() => setEditing(null)} data-testid="md-form-close" className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-4">
              {cfg.fields.map((f) => (
                <div key={f.key}>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{f.label}{f.required ? " *" : ""}</label>
                  {f.type === "grade_select" ? (
                    <select data-testid={f.testid} className={`${inputCls} bg-white`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                      <option value="">—</option>
                      {grades.map((g) => <option key={g.id} value={g.id}>{g.name || g.code}</option>)}
                    </select>
                  ) : f.type === "days" ? (
                    <div className="flex flex-wrap gap-2" data-testid={f.testid}>
                      {DAYS.map((d, i) => {
                        const on = (form.weekly_off || []).includes(i);
                        return (
                          <button type="button" key={d} data-testid={`md-day-${i}`}
                            onClick={() => setForm({ ...form, weekly_off: on ? form.weekly_off.filter((x) => x !== i) : [...(form.weekly_off || []), i] })}
                            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border transition-colors ${on ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <input type={f.type} required={f.required} data-testid={f.testid} className={inputCls} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                  )}
                </div>
              ))}
              {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="md-form-error">{formError}</p>}
              <button type="submit" disabled={busy} data-testid="md-form-submit"
                className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} {editing === "new" ? "Add" : "Save Changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterData;
